import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import uniqueId from "lodash/uniqueId";
import { updateContent } from "DAI/ContentDetails/services/apis";
import { checkAllAudiogramStatus, setAudiogramData } from "store/audiogramSlice";
// eslint-disable-next-line import/no-cycle
import { updateHeadline as setHeadline } from "store/dai/dashboardSlice";
import { checkAllVideoReelsStatus, setVideoReels } from "store/dai/videoreelSlice";
import { setToast } from "store/toastSlice";
import {
  addSpeakerlist,
  delSpeakerList,
  getAudiogramBackground,
  getInsights,
  replaceAllTranscript,
  updateSpeaker,
  updateSummaryChapters,
  updateTranscript,
} from "utils/apis";
import { analytics } from "utils/GTM";

const getClipboardCopyTitle = (title) => title;
const getClipboardCopySummary = (summary) => `[SUMMARY]:\n\n${summary}`;

const getClipboardCopyInsights = (insights, version) => {
  let insightsStr = "";

  if (version === null) {
    // Old format, iterate through list of insights
    insightsStr += "[CHAPTERS]\n\n";
    insightsStr += insights
      .map((insight) => `[${insight.timestamp}] ${insight.summary}`)
      .join("\n\n");
  } else {
    // Markdown
    insightsStr += insights;
  }

  return insightsStr;
};

const getClipboardCopyQuotes = (quotes, version) => {
  let quotesStr = "";

  if (version === null) {
    // Old format, iterate through list of quotes
    quotesStr += "[QUOTES]\n\n";
    quotesStr += quotes.map((quote) => `${quote.text}\n\n- ${quote.speaker}`).join("\n\n");
  } else {
    // Markdown
    quotesStr += quotes;
  }

  return quotesStr;
};

const getClipboardCopyTranscripts = (transcripts, fileVersion) => {
  let transcript = "[TRANSCRIPT]\n\n";

  if (fileVersion === null) {
    transcript += transcripts
      .map((t) => `[${t.timestamp}] (${t.speaker}): ${t.text}`)
      .join("\n\n");
  } else {
    transcript += transcripts
      // eslint-disable-next-line no-useless-escape
      .map((t) => `**${t.timestamp}** - \(${t.speaker}\): ${t.text}`)
      .join("\n\n");
  }

  return transcript;
};

const getClipboardCopyHeadlines = (headlines) => {
  let text = "[Title Suggestions]\n\n";

  if (headlines.youtube) {
    text += `Youtube Title Suggestions:\n\n`;
    text += headlines.youtube
      .map((v) => `\xa0\xa0 ${v}`)
      .join("\n\n");
  }

  if (headlines.podcast) {
    text += `\n\nPodcast Title Suggestions:\n\n`;
    text += headlines.podcast
      .map((v) => `\xa0\xa0 ${v}`)
      .join("\n\n");
  }

  return text;
};

const splitTranscriptTextIntoWords = (text) => text.split(" ").map((word) => ({ text: word.trim(), id: uniqueId() }));
const getTranscriptWithMappedWords = (transcript) => transcript.map((item) => ({
  ...item,
  words: item.text ? splitTranscriptTextIntoWords(item.text) : [],
}));

const transcriptSlice = createSlice({
  name: "transcript",
  initialState: {
    transcript: [],
    rawText: "",
    isTimestamped: false,
    insights: [],
    abstract: "",
    showNotes: "",
    article: null,
    tweet: null,
    caption: null,
    clipFinder: null,
    clipboardType: null,
    clipboardValue: null,
    attribute: {},
    quotes: [],
    speakerList: [],
    error: false,
    loading: false,
    isRecheck: false,
    quotesno: 5,
    formatTranscriptPlainText: false,
    searchTranscript: "",
    searchTimestamp: "",
    searchQuotes: "",
    searchAudioGram: "",
    tabvalue: 1,
    speakerIndex: null,
    isShowSpeaker: false,
    isShowModal: false,
    docId: null,
    speakerUpdateLoading: false,
    isEditSpeakerModel: false,
    selectedSpeakerName: "",
    audiogram: [],
    audiogramBackground: [],
    videogram: [],
    fileName: "",
    anchorEl: null,
    speakerName: "",
    selectedVideoUrl: "",
    isVideoreelsGenerated: false,
    selecteTimeStamp: null,
    findText: "",
    replaceText: "",
    foundTextIndex: [],
    textFoundCount: 0,
    currentHighlightedIndex: { section: 0, wordIndex: 0, currentPointer: 0 },
    displayFindAndReplace: false,
    isSaving: false,
    fileVersion: null,
    headlineLoading: false,
    seoKeywords: [],
    headlines: [],
    currentSentence: "",
    content_brain: "",
  },

  reducers: {
    initializeTanscripts: (state, action) => {
      const { speakers, transcript } = action.payload;

      state.transcript = transcript || [];
      state.isTimestamped = action.payload.isTimestamped;
      state.rawText = action.payload.rawText;
      state.speakerList = speakers || [];

      if (state.transcript.length) {
        state.transcript = getTranscriptWithMappedWords(state.transcript);
      }

      state.docId = action.payload.docId;
    },

    resetTranscripts: (state) => {
      state.transcript = [];
      state.docId = null;
      state.content_brain = "";
    },

    setAttributes: (state, action) => {
      state.attribute = action.payload;
    },

    setContent: (state, action) => {
      const { contentType } = action.payload;
      const [type, index] = contentType.split(".");

      if (index !== undefined) {
        state[type][index] = action.payload.text;
      } else {
        state[contentType] = action.payload.text;
      }
    },

    setError: (state, action) => {
      state.error = action.payload;
    },

    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    setIsRecheck: (state, action) => {
      state.isRecheck = action.payload;
    },

    setQuotesNo: (state, action) => {
      state.quotesno = action.payload;
    },
    setSearchTranscript: (state, action) => {
      state.searchTranscript = action.payload;
    },
    setSearchTimestamp: (state, action) => {
      state.searchTimestamp = action.payload;
    },
    setSearchQuotes: (state, action) => {
      state.searchQuotes = action.payload;
    },
    setSearchAudioGram: (state, action) => {
      state.searchAudioGram = action.payload;
    },
    setTabValue: (state, action) => {
      state.tabvalue = action.payload;
    },
    addSpeaker: (state, action) => {
      state.speakerList.push(action.payload);
    },
    delSpeaker: (state, action) => {
      state.speakerList = state.speakerList.filter(
        (speaker) => speaker !== action.payload,
      );
    },
    updateAllSpeaker: (state, action) => {
      state.speakerList = state.speakerList.map((speaker) => {
        if (speaker === action.payload.oldSpeaker) {
          return action.payload.newSpeaker;
        }

        return speaker;
      });
      state.transcript.map((transcript) => {
        if (transcript.speaker === action.payload.oldSpeaker) {
          transcript.speaker = action.payload.newSpeaker;
        }

        return transcript;
      });
    },
    changeDisplayTranscriptSpeaker: (state, action) => {
      state.transcript[action.payload.index].speaker = action.payload.speaker;
    },
    updateTrancript: (state, action) => {
      state.transcript[action.payload.index].text = action.payload.text;
      state.transcript[action.payload.index].words = splitTranscriptTextIntoWords(action.payload.text);
    },
    handShowModal: (state, action) => {
      state.isShowSpeaker = true;
      state.speakerIndex = action.payload.index;
      state.anchorEl = action.payload.anchorEl;
      state.speakerName = action.payload.speaker;
    },
    handCloseModal: (state) => {
      state.isShowModal = false;
      state.isShowSpeaker = false;
    },
    handleOpenModal: (state, action) => {
      if (action.payload.isNew) {
        state.selectedSpeakerName = "Add New Speaker";
        state.isEditSpeakerModel = false;
      } else {
        state.selectedSpeakerName = action.payload.speaker;
        state.isEditSpeakerModel = true;
      }

      state.isShowModal = true;
      state.isShowSpeaker = false;
    },
    isEditSpeakerModel: (state, action) => {
      state.isEditSpeakerModel = action.payload;
    },
    selectedSpeakerName: (state, action) => {
      state.selectedSpeakerName = action.payload;
    },
    speakerUpdateLoading: (state, action) => {
      state.speakerUpdateLoading = action.payload;
    },
    setSelectedVideoUrl: (state, action) => {
      state.selectedVideoUrl = action.payload;
    },
    updateChapters: (state, action) => {
      state.insights = action.payload;
    },
    updateSummary: (state, action) => {
      state.abstract = action.payload;
    },
    updateHeadline: (state, action) => {
      state.attribute.headline = action.payload;
    },
    setSelecteTimeStamp: (state, action) => {
      state.selecteTimeStamp = action.payload;
    },
    setFindText: (state, action) => {
      state.findText = action.payload;
      state.foundTextIndex = [];
      state.textFoundCount = 0;

      if (state.findText) {
        const regex = new RegExp(state.findText, "g");
        state.transcript.forEach((transcript, index) => {
          const match = transcript.text.match(regex);

          if (match) {
            const wordsFound = match.length;
            state.textFoundCount += wordsFound;
            state.foundTextIndex.push({
              index,
              textfound: match,
            });
          }
        });
      }
    },
    highlightForward: (state) => {
      if (state.foundTextIndex.length > 0) {
        let currentSection = state.currentHighlightedIndex.section;
        let { currentPointer } = state.currentHighlightedIndex;
        const { currentWordIndex } = state.currentHighlightedIndex;
        const currentSectionWordList = state.foundTextIndex[currentPointer].textfound;
        const nextWordIndex = currentWordIndex + 1;

        if (nextWordIndex < currentSectionWordList.length) {
          state.currentHighlightedIndex = {
            section: currentSection,
            wordIndex: nextWordIndex,
            currentPointer,
          };
        } else {
          currentPointer += 1;

          if (currentPointer === state.foundTextIndex.length) {
            currentPointer = 0;
          }

          currentSection = state.foundTextIndex[currentPointer].index;
          state.currentHighlightedIndex = {
            section: currentSection,
            wordIndex: 0,
            currentPointer,
          };
        }
      }
    },
    highlightBackward: (state) => {
      if (state.foundTextIndex.length > 0) {
        let currentSection = state.currentHighlightedIndex.section;
        let { currentPointer } = state.currentHighlightedIndex;
        const { currentWordIndex } = state.currentHighlightedIndex;
        const previousWordIndex = currentWordIndex - 1;

        if (previousWordIndex >= 0) {
          state.currentHighlightedIndex = {
            section: currentSection,
            wordIndex: previousWordIndex,
            currentPointer,
          };
        } else {
          currentPointer -= 1;

          if (currentPointer < 0) {
            currentPointer = state.foundTextIndex.length - 1;
          }

          currentSection = state.foundTextIndex[currentPointer].index;
          const newSectionWordList = state.foundTextIndex[currentPointer].textfound;
          state.currentHighlightedIndex = {
            section: currentSection,
            wordIndex: newSectionWordList.length - 1,
            currentPointer,
          };
        }
      }
    },

    setReplaceText: (state, action) => {
      state.replaceText = action.payload;
    },

    replaceTextHandler: (state) => {
      if (state.foundTextIndex.length > 0) {
        const currentSection = state.currentHighlightedIndex.section;
        const currentWordIndex = state.currentHighlightedIndex.wordIndex;
        const { currentPointer } = state.currentHighlightedIndex;
        const currentSectionWordList = state.foundTextIndex[currentPointer].textfound;
        const currentWord = currentSectionWordList[currentWordIndex];
        const currentWordLength = currentWord.length;
        let currentWordStartIndex = state.transcript[currentSection].text.indexOf(currentWord);

        for (let i = 1; i <= currentWordIndex; i++) {
          currentWordStartIndex = state.transcript[currentSection].text.indexOf(
            currentWord,
            currentWordStartIndex + 1,
          );
        }

        const currentWordEndIndex = currentWordStartIndex + currentWordLength;
        const beforeText = state.transcript[currentSection].text.substring(
          0,
          currentWordStartIndex,
        );
        const afterText = state.transcript[currentSection].text.substring(currentWordEndIndex);

        state.transcript[currentSection].text = beforeText + state.replaceText + afterText;
        state.transcript[currentSection].words = splitTranscriptTextIntoWords(state.transcript[currentSection].text);
      }
    },
    toggleDisplayFindAndReplace: (state) => {
      state.displayFindAndReplace = !state.displayFindAndReplace;
      state.findText = "";
      state.replaceText = "";
    },
    setFindAndReplace: (state, action) => {
      state.displayFindAndReplace = action.payload;
    },
    setTranscript: (state, action) => {
      state.transcript = action.payload;
      state.transcript = getTranscriptWithMappedWords(action.payload);
    },

    setIsSaving: (state, action) => {
      state.isSaving = action.payload;
    },

    setcontentBrain: (state, action) => {
      state.content_brain = action.payload;
    },

    copySummary: (state) => {
      state.clipboardType = "abstract";
      state.clipboardValue = getClipboardCopySummary(state.abstract);
    },

    copyShowNotes: (state) => {
      const { title } = state.attribute;
      const { abstract, fileVersion, insights, quotes, audiogram } = state;
      let quotesData;

      state.clipboardType = "showNotes";

      if (fileVersion === null) {
        // Old format
        quotesData = (quotes.length > 0) ? quotes : audiogram;

        state.clipboardValue = (
          `${getClipboardCopyTitle(title)}\n\n`
          + `${getClipboardCopySummary(abstract)}\n\n`
          + `${getClipboardCopyInsights(insights, fileVersion)}\n\n`
          + `${getClipboardCopyQuotes(quotesData, fileVersion)}\n\n`
        );
      } else {
        // Markdown
        state.clipboardValue = state.showNotes;
      }
    },

    copyQuotes: (state) => {
      const { audiogram, fileVersion, quotes } = state;
      let quotesData;

      state.clipboardType = "quotes";

      if (fileVersion === null) {
        if (quotes && quotes.length) {
          quotesData = quotes;
        } else {
          quotesData = audiogram;
        }
      } else {
        // Markdown string
        quotesData = quotes;
      }

      state.clipboardValue = getClipboardCopyQuotes(quotesData, fileVersion);
    },

    copyChapters: (state) => {
      const { insights, fileVersion } = state;

      state.clipboardType = "insights";
      state.clipboardValue = getClipboardCopyInsights(insights, fileVersion);
    },

    copyTranscript: (state) => {
      const { fileVersion } = state.attribute;

      state.clipboardType = "transcript";
      state.clipboardValue = getClipboardCopyTranscripts(state.transcript, fileVersion);
    },

    copySEOKeyword: (state) => {
      state.clipboardType = "seoKeywords";
      state.clipboardValue = state.seoKeywords.join(", ");
    },

    copyHeadlines: (state) => {
      state.clipboardType = "headlines";
      state.clipboardValue = getClipboardCopyHeadlines(state.headlines);
    },

    copyContentBrain: (state) => {
      state.clipboardType = "content_brain";
      state.clipboardValue = state.content_brain;
    },

    copyContent: (state, action) => {
      state.clipboardType = action.payload.type;
      state.clipboardValue = state[action.payload.type] || " ";
    },
    setHeadlineLoading: (state, action) => {
      state.headlineLoading = action.payload;
    },
    setCurrentSentence: (state, action) => {
      state.currentSentence = action.payload;
    },
  },
});

export const {
  initializeTanscripts,
  resetTranscripts,
  setAttributes,
  setContent,
  setError,
  setLoading,
  setIsRecheck,
  setQuotesNo,
  setSearchTranscript,
  setSearchTimestamp,
  setSearchQuotes,
  setTabValue,
  addSpeaker,
  changeDisplayTranscriptSpeaker,
  delSpeaker,
  updateAllSpeaker,
  handShowModal,
  handCloseModal,
  handleOpenModal,
  isEditSpeakerModel,
  selectedSpeakerName,
  speakerUpdateLoading,
  updateTrancript,
  setOpenAudioGramPreview,
  setSearchAudioGram,
  setintervalId,
  setSelectedVideoUrl,
  updateChapters,
  updateSummary,
  updateHeadline,
  setSelecteTimeStamp,
  setFindText,
  textFoundCount,
  setReplaceText,
  setCurrentHighlightedIndex,
  setHighlightedText,
  highlightForward,
  highlightBackward,
  replaceTextHandler,
  setFindAndReplace,
  toggleDisplayFindAndReplace,
  setTranscript,
  setIsSaving,
  copySummary,
  copyShowNotes,
  copyQuotes,
  copyChapters,
  copyTranscript,
  copySEOKeyword,
  copyContent,
  setHeadlineLoading,
  copyHeadlines,
  setCurrentSentence,
  setcontentBrain,
  copyContentBrain,
} = transcriptSlice.actions;
export const transcriptSelector = (state) => state.transcript.transcript;
export const rawTextSelector = (state) => state.transcript.rawText;
export const isTimestampedSelector = (state) => state.transcript.isTimestamped;
export const transcriptsSelector = (state) => state.transcript;
export const insightsSelector = (state) => state.transcript.insights;
export const abstractSelector = (state) => state.transcript.abstract;
export const selectShowNotes = (state) => state.transcript.showNotes;
export const selectArticle = (state) => state.transcript.article;
export const selectTweet = (state) => state.transcript.tweet;
export const selectCaption = (state) => state.transcript.caption;
export const selectClipFinder = (state) => state.transcript.clipFinder;
export const isSaving = (state) => state.transcript.isSaving;
export const quotesSelector = (state) => state.transcript.quotes;
export const speakerListSelector = (state) => state.transcript.speakerList;
export const errorSelector = (state) => state.transcript.error;
export const loadingSelector = (state) => state.transcript.loading;
export const selectIsRecheck = (state) => state.transcript.isRecheck;
export const quotesNoSelector = (state) => state.transcript.quotesno;
export const formatTranscriptPlainTextSelector = (state) => state.transcript.formatTranscriptPlainText;
export const searchTimestampSelector = (state) => state.transcript.searchTimestamp;
export const searchTranscriptSelector = (state) => state.transcript.searchTranscript;
export const searchQuotesSelector = (state) => state.transcript.searchQuotes;
export const tabValueSelector = (state) => state.transcript.tabvalue;
export const speakerIndexSelector = (state) => state.transcript.speakerIndex;
export const isShowSpeakerSelector = (state) => state.transcript.isShowSpeaker;
export const isShowModalSelector = (state) => state.transcript.isShowModal;
export const isEditSpeakerModelSelector = (state) => state.transcript.isEditSpeakerModel;
export const selectedSpeakerNameSelector = (state) => state.transcript.selectedSpeakerName;
export const speakerUpdateLoadingSelector = (state) => state.transcript.speakerUpdateLoading;
export const audiogramSelector = (state) => state.transcript.audiogram;
export const audiogramBackgroundSelector = (state) => state.transcript.audiogramBackground;
export const searchAudioGramSelector = (state) => state.transcript.searchAudioGram;
export const videogramSelector = (state) => state.transcript.videogram;
export const anchorElSelector = (state) => state.transcript.anchorEl;
export const speakerNameSelector = (state) => state.transcript.speakerName;
export const SelectedVideoUrlSelector = (state) => state.transcript.selectedVideoUrl;
export const isVideoreelsGeneratedSelector = (state) => state.transcript.isVideoreelsGenerated;
export const selecteTimeStampSelector = (state) => state.transcript.selecteTimeStamp;
export const transcriptRefSelector = (state) => state.transcript.transcriptRef;
export const findTextSelector = (state) => state.transcript.findText;
export const textFoundCountSelector = (state) => state.transcript.textFoundCount;
export const replaceTextSelector = (state) => state.transcript.replaceText;
export const foundTextIndexSelector = (state) => state.transcript.foundTextIndex;
export const currentHighlightedIndexSelector = (state) => state.transcript.currentHighlightedIndex;
export const displayFindAndReplaceSelector = (state) => state.transcript.displayFindAndReplace;
export const selectContentByType = (type) => (state) => state.transcript[type];
export const getFileVersion = (state) => state.transcript.fileVersion;
export const selectClipboard = (state) => ({
  type: state.transcript.clipboardType,
  value: state.transcript.clipboardValue,
});
export const selectHeadlineLoading = (state) => state.transcript.headlineLoading;
export const seoKeywordsSelector = (state) => state.transcript.seoKeywords;
export const headlingSuggestionSelector = (state) => state.transcript.headlines;
export const currentSentenceSelector = (state) => state.transcript.currentSentence;
export const getTranscript = createAsyncThunk(
  "transcript/fetchTranscript",
  async (payload, thunkAPI) => {
    const { id, isRecheck = false } = payload;
    const { dispatch, getState } = thunkAPI;
    const { transcript } = getState();
    const { loading } = transcript;

    if (loading) {
      return;
    }

    dispatch(setLoading(true));
    dispatch(setIsRecheck(isRecheck));

    try {
      const response = await getInsights(id);

      dispatch(initializeTanscripts(response));

      if (response.error) {
        dispatch(setError(true));
        dispatch(setToast({ message: response.error, severity: "error" }));
      }

      const bg = await getAudiogramBackground();

      dispatch(
        setAudiogramData({
          audiogram: response.es_doc?.audiogram,
          audiogramBackground: bg || [],
          audioFile: response.es_doc?.serving_url,
          docId: id,
        }),
      );

      dispatch(setVideoReels(response.es_doc?.videogram));

      if (response.es_doc?.audiogram && response.es_doc?.audiogram.length > 0) {
        dispatch(checkAllAudiogramStatus(id));
      }

      if (response.es_doc?.videogram && response.es_doc?.videogram.length > 0) {
        dispatch(checkAllVideoReelsStatus(id));
      }
    } catch (e) {
      dispatch(setError(true));
      dispatch(setToast({ message: e.message, severity: "error" }));
    } finally {
      dispatch(setLoading(false));
    }
  },
);
export const addSpeakerAsync = createAsyncThunk(
  "transcript/addSpeaker",
  async (payload, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const {
      transcript: { docId },
    } = getState();

    dispatch(speakerUpdateLoading(true));

    try {
      await addSpeakerlist(docId, {
        speaker: payload.speaker,
      });

      dispatch(addSpeaker(payload.speaker));
    } catch (e) {
      dispatch(setToast({ message: e.message, severity: "error" }));
    }

    dispatch(speakerUpdateLoading(false));
    dispatch(handCloseModal());
  },
);
export const delSpeakerAsync = createAsyncThunk(
  "transcript/delSpeaker",
  async (payload, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const {
      transcript: { docId },
    } = getState();
    dispatch(speakerUpdateLoading(true));

    try {
      await delSpeakerList(docId, {
        speaker: payload.speaker,
      });

      dispatch(delSpeaker(payload.speaker));
    } catch (e) {
      dispatch(setToast({ message: e.message, severity: "error" }));
    }

    dispatch(speakerUpdateLoading(false));
    dispatch(handCloseModal());
  },
);
export const updateAllSpeakerAsync = createAsyncThunk(
  "transcript/updateAllSpeaker",
  async (payload, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const {
      transcript: { docId },
    } = getState();
    const newPayload = {
      current_speaker: payload.oldSpeaker,
      new_speaker: payload.newSpeaker,
      index: payload.index,
    };

    if (payload.isall) {
      // Don't send index if everything needs to be replaced
      newPayload.replace_all = payload.isall;

      delete newPayload.index;
    }

    dispatch(speakerUpdateLoading(true));

    try {
      await updateSpeaker(docId, newPayload);

      if (payload.isall) {
        dispatch(updateAllSpeaker(payload));
      } else {
        dispatch(
          changeDisplayTranscriptSpeaker({
            index: payload.index,
            speaker: payload.newSpeaker,
          }),
        );
      }
    } catch (e) {
      dispatch(setToast({ message: e.message, severity: "error", autoClose: true }));
    } finally {
      dispatch(speakerUpdateLoading(false));
    }

    dispatch(handCloseModal());
  },
);
export const updateSpecficTrancript = createAsyncThunk(
  "transcript/updateSpecficTrancript",
  async (payload, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const {
      transcript: { docId },
    } = getState();

    try {
      await updateTranscript(docId, payload);
      dispatch(
        updateTrancript({
          index: payload.index,
          text: payload.text,
        }),
      );
      analytics.track("Transcript Edited Successfully");
      dispatch(setToast({ message: "Transcript Updated", severity: "success", autoClose: true }));
    } catch (e) {
      dispatch(setToast({ message: e.message, severity: "error", autoClose: true }));
    }
  },
);
export const replaceSpecficTrancript = createAsyncThunk(
  "transcript/replaceSpecficTrancript",
  async (payload, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    dispatch(replaceTextHandler());
    const {
      transcript: { docId, currentHighlightedIndex, transcript },
    } = getState();
    const index = currentHighlightedIndex.section;
    const { text } = transcript[index];

    try {
      await updateTranscript(docId, {
        index,
        text,
      });
      dispatch(setToast({ message: "Replaced", severity: "success", autoClose: true }));
    } catch (e) {
      dispatch(setToast({ message: e.message, severity: "error", autoClose: true }));
    }
  },
);
export const replaceAll = createAsyncThunk(
  "transcript/replaceAllTranscript",
  async (payload, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const {
      transcript: { docId, findText, replaceText },
    } = getState();

    try {
      const response = await replaceAllTranscript(docId, {
        find: findText,
        replace: replaceText,
      });
      dispatch(setTranscript(response));
      dispatch(setReplaceText(""));
      dispatch(setFindText(""));
      dispatch(setToast({ message: "Replaced All", severity: "success", autoClose: true }));
    } catch (e) {
      dispatch(setToast({ message: e.message, severity: "error", autoClose: true }));
    }
  },
);
export const updateChaptersAndSummary = createAsyncThunk(
  "transcript/updateChaptersAndSummary",
  async (payload, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const {
      transcript: { docId, abstract, insights },
    } = getState();
    const constructedPayload = {};
    const property = Object.keys(payload)[0];

    if (property === "summary") {
      constructedPayload.summary = payload.summary;
      constructedPayload.chapters = insights;
    } else if (property === "chapters") {
      constructedPayload.summary = abstract;
      constructedPayload.chapters = payload.chapters;
    } else if (property === "headline") {
      constructedPayload.summary = abstract;
      constructedPayload.chapters = insights;
      constructedPayload.headline = payload.headline;

      dispatch(setHeadlineLoading(true));
    }

    try {
      await updateSummaryChapters(docId, constructedPayload);

      switch (property) {
        case "summary":
          dispatch(updateSummary(payload.summary));
          break;
        case "chapters":
          dispatch(updateChapters(payload.chapters));
          break;
        case "headline":
          dispatch(updateHeadline(payload.headline));
          dispatch(setHeadline({ id: docId, headline: payload.headline }));
          dispatch(setHeadlineLoading(false));
          break;
        default:
          break;
      }

      analytics.track(`${property} edited successfully`);
      dispatch(
        setToast({
          message: `${property} updated`,
          severity: "success",
          autoClose: true,
        }),
      );
    } catch (e) {
      dispatch(setToast({ message: e.message, severity: "error" }));
      dispatch(setHeadlineLoading(false));
    }
  },
);
export const updateContentItem = createAsyncThunk("transcript/updateContent", async (payload, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const { transcript } = getState();
  // eslint-disable-next-line no-shadow
  const { docId, isSaving } = transcript;
  const { content, type } = payload;

  if (isSaving) {
    return;
  }

  dispatch(setIsSaving(true));

  if (type === "headline") {
    dispatch(setHeadlineLoading(true));
  }

  try {
    await updateContent(docId, type, content);

    let contentType = "";

    // We have to handle naming inconsistencies across the backend.
    // Some features are interchangably called "summary/abstract",
    // "show_notes/showNotes", "chapters/insights" etc.
    switch (type) {
      case "show_notes":
        contentType = "showNotes";
        break;
      case "summary":
        contentType = "abstract";
        break;
      case "chapters":
      case "insight":
        contentType = "insights";
        break;
      case "headline":
        contentType = "attribute.headline";
        break;
      default:
        contentType = type;
    }

    dispatch(setContent({ contentType, text: content }));
    analytics.track(`${type} edited successfully`);
  } catch (e) {
    dispatch(
      setToast({
        message: "Content could not be updated at this time. Please retry in a bit.",
        severity: "error",
      }),
    );
  } finally {
    dispatch(setIsSaving(false));
    dispatch(setHeadlineLoading(false));
  }
});
export const copyItem = createAsyncThunk("transcript/copy", async (payload, thunkAPI) => {
  const { type } = payload;
  const { dispatch } = thunkAPI;

  switch (type) {
    case "abstract":
      dispatch(copySummary());
      break;
    case "showNotes":
      dispatch(copyShowNotes());
      break;
    case "quotes":
      dispatch(copyQuotes());
      break;
    case "transcript":
      dispatch(copyTranscript());
      break;
    case "insights":
      dispatch(copyChapters());
      break;
    case "seoKeywords":
      dispatch(copySEOKeyword());
      break;
    case "headlines":
      dispatch(copyHeadlines());
      break;
    case "content_brain":
      dispatch(copyContentBrain());
      break;
    default:
      dispatch(copyContent({ type }));
  }
});
export default transcriptSlice.reducer;
